import { AdminApiError, type AdminClient, type AuditEntry } from '@apx/admin-sdk';
import { fmtDateTime } from '../format.js';
import { exportXlsx } from '../xlsx.js';
import { applySort, getSortState, sortableTh, wireSortHeaders } from '../sort.js';
import { mountSavedViews } from '../saved-views.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { html, infoModal } from '../modal.js';

export interface AuditPageDeps {
  client: AdminClient;
  integratorId: string;
}

/** Readable action categories → the substring the backend matches (`contains`).
 *  Admins pick a category instead of guessing the raw dotted action string. */
const ACTION_CATEGORIES: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'All actions', value: '' },
  { label: 'Config approvals (approve/reject)', value: 'game_config' },
  { label: 'Game registry', value: 'game.registry' },
  { label: 'Game catalog', value: 'game_catalog' },
  { label: 'Auto-play', value: 'auto_play' },
  { label: 'Jackpot', value: 'jackpot' },
  { label: 'Player changes', value: 'player' },
  { label: 'Wallet / balance', value: 'wallet' },
  { label: 'Bet refunds', value: 'bet.refund' },
  { label: 'Integrator changes', value: 'integrator' },
  { label: 'Admin users', value: 'admin_user' },
  { label: 'Admin MFA', value: 'admin.mfa' },
];

export async function renderAuditPage(root: HTMLElement, deps: AuditPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Audit log</h1>
    <p class="page-sub">Unified <code>AdminAudit</code> stream. Every approval, player mutation, config write, balance adjustment, jackpot reseed, and integrator change lands here with actor + before/after jsonb.</p>

    <div class="card report-filters">
      <h2>Filters</h2>
      <div class="filter-row">
        <label class="filter-field">Action
          <select id="a-action" class="field-input">
            ${ACTION_CATEGORIES.map((c) => `<option value="${c.value}">${c.label}</option>`).join('')}
          </select>
        </label>
        <label class="filter-field">Actor
          <select id="a-actor" class="field-input"><option value="">Any admin</option></select>
        </label>
        <label class="filter-field">Limit
          <input id="a-limit" type="number" min="10" max="200" value="50" class="field-input" style="width:90px" />
        </label>
        <button id="a-run" class="primary">Apply</button>
        <button id="a-export" class="ghost">Export Excel</button>
        <div id="a-views" class="row" style="margin:0 0 0 auto;gap:6px"></div>
      </div>
    </div>

    <div class="card">
      <h2>Entries</h2>
      <div id="a-list">${skeletonTable({ rows: 8, columns: 5 })}</div>
    </div>
  `;

  const listEl = root.querySelector<HTMLElement>('#a-list')!;

  let accumulated: AuditEntry[] = [];
  let lastCursor: string | undefined;
  let lastAction: string | undefined;
  let lastActor: string | undefined;
  let lastLimit = 50;

  // Populate the Actor dropdown from the admin-user roster so admins pick a
  // name, not a UUID. Non-fatal if it fails (the "Any admin" default stays).
  const actorSel = root.querySelector<HTMLSelectElement>('#a-actor')!;
  void deps.client
    .listAdminUsers()
    .then((users) => {
      actorSel.insertAdjacentHTML(
        'beforeend',
        users
          .map((u) => `<option value="${escapeHtml(u.id)}">${escapeHtml(u.email)}</option>`)
          .join(''),
      );
    })
    .catch(() => {
      /* leave "Any admin" only */
    });

  function afterRender(): void {
    listEl
      .querySelector<HTMLButtonElement>('#a-load-more')
      ?.addEventListener('click', () => void refresh(true));
    const table = listEl.querySelector<HTMLElement>('table');
    if (table) wireSortHeaders(table, 'audit', () => rerenderInPlace());
    bindRowDetails();
  }

  function bindRowDetails(): void {
    listEl.querySelectorAll<HTMLTableRowElement>('tr[data-audit-id]').forEach((row) => {
      row.addEventListener('click', () => {
        const entry = accumulated.find((e) => e.id === row.dataset.auditId);
        if (entry) openAuditDetail(entry);
      });
    });
  }

  function rerenderInPlace(): void {
    listEl.innerHTML = `<div style="max-height:62vh;overflow:auto">${renderTable(accumulated)}</div>
      <div class="row" style="margin-top:10px">
        ${lastCursor ? '<button id="a-load-more">Load more</button>' : '<span class="muted">End of log.</span>'}
        <span class="muted" style="margin-left:auto">${accumulated.length} loaded</span>
      </div>`;
    afterRender();
  }

  async function refresh(append = false): Promise<void> {
    if (!append) {
      accumulated = [];
      lastCursor = undefined;
      lastAction = root.querySelector<HTMLSelectElement>('#a-action')!.value.trim() || undefined;
      lastActor = root.querySelector<HTMLSelectElement>('#a-actor')!.value.trim() || undefined;
      lastLimit = Number(root.querySelector<HTMLInputElement>('#a-limit')!.value) || 50;
      listEl.innerHTML = skeletonTable({ rows: 8, columns: 5 });
    }
    try {
      const res = await deps.client.listAuditLog({
        integratorId: deps.integratorId,
        action: lastAction,
        actorUserId: lastActor,
        limit: lastLimit,
        cursor: append ? lastCursor : undefined,
      });
      accumulated = accumulated.concat(res.entries);
      lastCursor = res.nextCursor;
      listEl.innerHTML = `<div style="max-height:62vh;overflow:auto">${renderTable(accumulated)}</div>
        <div class="row" style="margin-top:10px">
          ${lastCursor ? '<button id="a-load-more">Load more</button>' : '<span class="muted">End of log.</span>'}
          <span class="muted" style="margin-left:auto">${accumulated.length} loaded</span>
        </div>`;
      afterRender();
    } catch (err) {
      listEl.innerHTML = `<p class="error">${err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)}</p>`;
    }
  }

  function renderTable(entries: AuditEntry[]): string {
    if (entries.length === 0) return '<p class="muted">No matching entries.</p>';
    const sort = getSortState('audit');
    const sorted = applySort(entries, sort, (e, key) => {
      switch (key) {
        case 'createdAt':
          return e.createdAt;
        case 'actorEmail':
          return e.actorEmail;
        case 'action':
          return e.action;
        case 'targetType':
          return e.targetType;
        default:
          return '';
      }
    });
    return `<table>
      <thead><tr>
        ${sortableTh('When', 'createdAt', sort)}
        ${sortableTh('Actor', 'actorEmail', sort)}
        ${sortableTh('Action', 'action', sort)}
        ${sortableTh('Target', 'targetType', sort)}
        <th>Diff</th>
        <th></th>
      </tr></thead>
      <tbody>${sorted
        .map((e) => {
          const diff = JSON.stringify({ before: e.before, after: e.after });
          const preview = diff.length > 140 ? `${diff.slice(0, 140)}…` : diff;
          return `<tr data-audit-id="${escapeHtml(e.id)}" style="cursor:pointer">
            <td>${fmtDateTime(e.createdAt)}</td>
            <td>${escapeHtml(e.actorEmail)}<br><span class="muted" style="font-size:11px"><code>${e.actorUserId.slice(0, 8)}…</code></span></td>
            <td><code>${escapeHtml(e.action)}</code></td>
            <td><code>${escapeHtml(e.targetType)}</code><br><span class="muted" style="font-size:11px">${escapeHtml(e.targetId)}</span></td>
            <td><div class="muted" style="max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px">${escapeHtml(preview)}</div></td>
            <td style="text-align:right"><span class="muted" style="font-size:11px">Details →</span></td>
          </tr>`;
        })
        .join('')}</tbody>
    </table>`;
  }

  function openAuditDetail(e: AuditEntry): void {
    const kv = (k: string, v: string): ReturnType<typeof html> =>
      html`<div><div class="detail-k">${k}</div><div class="detail-v">${v}</div></div>`;
    const json = (label: string, value: unknown): ReturnType<typeof html> => {
      const text = value == null ? '—' : JSON.stringify(value, null, 2);
      return html`<div class="detail-section-h">${label}</div><pre class="detail-json">${text}</pre>`;
    };
    infoModal({
      title: e.action,
      subtitle: `${e.targetType} · ${fmtDateTime(e.createdAt)}`,
      wide: true,
      body: html`
        <div class="detail-grid">
          ${kv('Audit id', e.id)}
          ${kv('When', fmtDateTime(e.createdAt))}
          ${kv('Actor', e.actorEmail)}
          ${kv('Actor user id', e.actorUserId)}
          ${kv('Integrator', e.integratorId ?? '—')}
          ${kv('Action', e.action)}
          ${kv('Target type', e.targetType)}
          ${kv('Target id', e.targetId)}
        </div>
        ${json('Before', e.before)}
        ${json('After', e.after)}
      `,
    });
  }

  root.querySelector<HTMLButtonElement>('#a-run')!.addEventListener('click', () => void refresh());

  mountSavedViews(root.querySelector<HTMLElement>('#a-views')!, 'audit', {
    capture: () => ({
      action: root.querySelector<HTMLSelectElement>('#a-action')!.value,
      actor: root.querySelector<HTMLSelectElement>('#a-actor')!.value,
      limit: root.querySelector<HTMLInputElement>('#a-limit')!.value,
    }),
    apply: (values) => {
      root.querySelector<HTMLSelectElement>('#a-action')!.value = values.action ?? '';
      root.querySelector<HTMLSelectElement>('#a-actor')!.value = values.actor ?? '';
      root.querySelector<HTMLInputElement>('#a-limit')!.value = values.limit ?? '50';
      void refresh();
    },
  });

  root.querySelector<HTMLButtonElement>('#a-export')!.addEventListener('click', () => {
    if (accumulated.length === 0) {
      showToast('No audit entries to export.', 'warn');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    exportXlsx(
      `apx-audit-${stamp}.xlsx`,
      accumulated.map((e) => ({
        id: e.id,
        createdAt: e.createdAt,
        actorEmail: e.actorEmail,
        actorUserId: e.actorUserId,
        integratorId: e.integratorId ?? '',
        action: e.action,
        targetType: e.targetType,
        targetId: e.targetId,
        before: JSON.stringify(e.before),
        after: JSON.stringify(e.after),
      })),
    );
    showToast(`Exported ${accumulated.length} audit entries.`, 'success');
  });

  await refresh();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
